/** The shared vocabulary every page is built from. */
import { cloneElement, isValidElement, useId, useMemo, useState, useEffect } from 'react';
import { Icon } from '../lib/icons.jsx';
import { loadPhoto, cachedPhoto, onPhotoChange } from '../lib/photos.js';
import {
  initials, humanise, inr, inrShort, fmtDate, fmtTime, num, pct,
  attendanceWorkingMinutes, fmtWorkingHrs, personOrgSubline,
} from '../lib/format.js';

export { humanise, inr, inrShort, num, pct };
export { fmtDate as shortDate, fmtTime as timeOnly };

/* --------------------------------------------------------------------- cards */

export function Card({ title, subtitle, actions, children, flush = false, tight = false, style, className = '' }) {
  return (
    <div className={`card ${className}`} style={style}>
      {(title || actions) && (
        <div className="card-h">
          <div>
            {title && <h3>{title}</h3>}
            {subtitle && <div className="sub">{subtitle}</div>}
          </div>
          {actions && <div className="right">{actions}</div>}
        </div>
      )}
      <div className={`card-b${flush ? ' flush' : ''}${tight ? ' tight' : ''}`}>{children}</div>
    </div>
  );
}

export function PageHead({ title, subtitle, actions }) {
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

/* ---------------------------------------------------------------- stat tiles */

export function Stat({ label, value, unit, sub, delta, dir, icon, spark }) {
  return (
    <div className="stat">
      {icon && <div className="stat-ico"><Icon name={icon} /></div>}
      <div className="lbl">{label}</div>
      <div className="val">{value}{unit ? <small> {unit}</small> : null}</div>
      {delta
        ? (
          <div className={`delta ${dir ?? 'flat'}`}>
            {dir === 'up' && <Icon name="arrowUp" strokeWidth={2.4} />}
            {dir === 'dn' && <Icon name="arrowDn" strokeWidth={2.4} />}
            <span>{delta}</span>
          </div>
        )
        : sub ? <div className="txt-sm muted">{sub}</div> : null}
      {spark && <div className="spark">{spark}</div>}
    </div>
  );
}

/* -------------------------------------------------------------------- badges */

const BADGE_TONE = {
  ACTIVE: 'good', PRESENT: 'good', APPROVED: 'good', RESOLVED: 'good', ONLINE: 'good', ACHIEVED: 'good',
  ON_TRACK: 'good', VERIFIED: 'good', PAID: 'good', SHARED: 'good', CALIBRATED: 'good',
  PENDING: 'warn', NOTICE_PERIOD: 'warn', HALF_DAY: 'warn', DEGRADED: 'warn', AT_RISK: 'warn',
  IN_PROGRESS: 'warn', DRAFT: 'warn', MEDIUM: 'warn', HIGH: 'warn', WARNING: 'warn', PROCESSING: 'warn',
  DECOMMISSIONED: 'crit',
  CRITICAL: 'crit', BREACHED: 'crit',
  ON_LEAVE: 'info', ONBOARDING: 'info', WAITING_ON_EMPLOYEE: 'info', PENDING_REGULARISATION: 'info',
  WFH: 'info', INFO: 'info', NATIONAL: 'info',
};

export function Badge({ children, tone, value, icon, style }) {
  const resolved = tone ?? BADGE_TONE[value] ?? '';
  return (
    <span className={`badge ${resolved}`} style={style}>
      {icon && <Icon name={icon} strokeWidth={2.2} />}
      {children ?? humanise(value)}
    </span>
  );
}

/** The dotted variant the prototype used for record status. */
export function StatusBadge({ value, children }) {
  const tone = BADGE_TONE[value] ?? '';
  return <span className={`badge ${tone}`}><span className="bdot" />{children ?? humanise(value)}</span>;
}

/**
 * Attendance status with working hours on the badge.
 * Closed days: last out − first in. Open WFH / web check-in: time since punch-in.
 */
export function AttendanceStatusBadge({
  status,
  value,
  firstInAt,
  lastOutAt,
  workMinutes,
  workMode,
  source,
  children,
}) {
  const resolved = status ?? value;
  const mins = attendanceWorkingMinutes({ firstInAt, lastOutAt, workMinutes, workMode, source });
  const hrs = fmtWorkingHrs(mins);
  const label = children
    ?? (hrs ? `${humanise(resolved)} · ${hrs}` : humanise(resolved));
  return <StatusBadge value={resolved}>{label}</StatusBadge>;
}

export function Chip({ children, on, onClick, icon }) {
  return (
    <button type="button" className={`chip${on ? ' on' : ''}`} onClick={onClick}>
      {icon && <Icon name={icon} />}
      {children}
    </button>
  );
}

/* -------------------------------------------------------------------- tables */

function compareSortValues(a, b) {
  const aEmpty = a == null || a === '';
  const bEmpty = b == null || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function Table({ columns, rows, empty = 'Nothing to show yet', emptySub, onRowClick, footer }) {
  const [sort, setSort] = useState(null);
  // Group header rows (`__kind: 'group'`) keep the caller’s order — sorting would
  // scatter section headers through employee lines.
  const hasGroups = rows.some((r) => r?.__kind === 'group');
  const ordered = useMemo(() => {
    if (hasGroups || !sort) return rows;
    const column = columns.find((c) => c.key === sort.key);
    if (!column?.sortValue) return rows;
    const dir = sort.dir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
      const av = column.sortValue(a);
      const bv = column.sortValue(b);
      const aEmpty = av == null || av === '';
      const bEmpty = bv == null || bv === '';
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      return compareSortValues(av, bv) * dir;
    });
  }, [columns, rows, sort, hasGroups]);

  const toggleSort = (key) => {
    if (hasGroups) return;
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };

  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          <tr>
            {columns.map((c) => {
              const active = sort?.key === c.key;
              return (
                <th
                  key={c.key}
                  className={c.numeric ? 'num' : ''}
                  style={c.width ? { width: c.width } : undefined}
                  aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {typeof c.sortValue === 'function' && !hasGroups ? (
                    <button type="button" className="tbl-sort" onClick={() => toggleSort(c.key)}>
                      {c.label}
                      {active && <Icon name={sort.dir === 'asc' ? 'arrowUp' : 'arrowDn'} />}
                    </button>
                  ) : c.label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {ordered.length === 0 ? (
            <tr>
              <td colSpan={columns.length}>
                <Empty icon="search" title={empty} sub={emptySub} />
              </td>
            </tr>
          ) : ordered.map((row, index) => {
            // Plain section label (legacy). Manager trees use `__asRow` so the
            // header can show the same attendance columns as employees.
            if (row?.__kind === 'group' && !row.__asRow) {
              return (
                <tr key={row.id ?? `group-${index}`} className="tbl-group">
                  <td colSpan={columns.length}>
                    <span className="tbl-group-label">{row.label}</span>
                    {row.count != null && <span className="tbl-group-cnt">{row.count}</span>}
                  </td>
                </tr>
              );
            }
            const isGroup = row?.__kind === 'group';
            const clickable = Boolean(onRowClick);
            return (
              <tr
                key={row.id ?? index}
                className={[
                  isGroup ? 'tbl-group tbl-group-row' : null,
                  row?.__nested ? 'tbl-nested' : null,
                  clickable ? 'pointer' : null,
                ].filter(Boolean).join(' ') || undefined}
                onClick={clickable ? (e) => { if (!e.target.closest('button,a,input')) onRowClick(row); } : undefined}
              >
                {columns.map((c) => (
                  <td key={c.key} className={c.numeric ? 'num' : ''}>
                    {c.render ? c.render(row) : row[c.key] ?? '—'}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {footer}
    </div>
  );
}

/* -------------------------------------------------------------------- people */

/**
 * Somebody's face, or their initials until it arrives.
 *
 * Pass `employeeId` and the photo loads itself. Initials stay as the fallback
 * rather than a silhouette: with no photo they still tell two colleagues apart,
 * and they are what the interface showed before photos existed, so a directory
 * mid-rollout looks intentional rather than half-loaded.
 */
export function Avatar({ name, size = 'sm', ring = false, employeeId, hasPhoto = true, src: srcOverride }) {
  const [src, setSrc] = useState(() => srcOverride ?? cachedPhoto(employeeId) ?? null);

  useEffect(() => {
    if (srcOverride !== undefined) {
      setSrc(srcOverride || null);
      return undefined;
    }
    if (!employeeId) return undefined;
    let alive = true;
    loadPhoto(employeeId, hasPhoto).then((url) => { if (alive) setSrc(url); });
    // Redraw when this person's photo is replaced anywhere in the app. Always
    // asks the network on that path: the record in hand says there was no
    // photo a moment ago, which is exactly what has just stopped being true.
    const off = onPhotoChange((changed) => {
      if (changed !== employeeId) return;
      loadPhoto(employeeId, true).then((url) => { if (alive) setSrc(url); });
    });
    return () => { alive = false; off(); };
  }, [employeeId, hasPhoto, srcOverride]);

  return (
    <div className={`avatar av-${size}${ring ? ' av-ring' : ''}${src ? ' av-photo' : ''}`} title={name}>
      {src ? <img src={src} alt="" /> : initials(name)}
    </div>
  );
}

/**
 * Name + optional org subline.
 *
 * Prefer `department` + `dateOfJoining` for the standard list look:
 *   Employee Name
 *   department, 2y 3m 12d
 * Pass `sub` only when you need a one-off line (email, designation, etc.).
 */
export function Person({
  name,
  sub,
  size,
  employeeId,
  hasPhoto,
  department,
  dateOfJoining,
}) {
  const orgSub = personOrgSubline(department, dateOfJoining);
  const line = sub !== undefined && sub !== null && sub !== ''
    ? sub
    : (orgSub || null);
  return (
    <div className="cell-person">
      <Avatar name={name} size={size} employeeId={employeeId} hasPhoto={hasPhoto} />
      <div style={{ minWidth: 0 }}>
        <div className="nm">{name}</div>
        {line ? <div className="sub">{line}</div> : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- meters */

export function Meter({ value, tone, label }) {
  return (
    <div className="meter-row">
      {label && <div className="txt-sm" style={{ width: 120 }}>{label}</div>}
      <div className="pbar"><span style={{ width: `${Math.min(100, Math.max(0, value))}%`, background: tone }} /></div>
      <div className="mv">{Math.round(value)}%</div>
    </div>
  );
}

export function Progress({ value, tone, thin = false }) {
  return (
    <div className={`pbar${thin ? ' thin' : ''}`}>
      <span style={{ width: `${Math.min(100, Math.max(0, value))}%`, background: tone }} />
    </div>
  );
}

/* ---------------------------------------------------------------------- tabs */

export function Tabs({ tabs, value, onChange }) {
  return (
    <div className="tabs">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          className={`tab${value === t.id ? ' on' : ''}`}
          onClick={() => onChange(t.id)}
        >
          {t.icon && <Icon name={t.icon} />}
          {t.label}
          {t.count ? <span className="cnt">{t.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ timeline */

export function Timeline({ items }) {
  return (
    <div className="tl">
      {items.map((it, i) => (
        <div key={i} className={`tl-item ${it.tone ?? ''}`}>
          <div className="tl-t">{it.title}</div>
          {it.sub && <div className="tl-s">{it.sub}</div>}
        </div>
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------- checklist */

export function CheckItem({ done, title, sub, onToggle, actions, highlight }) {
  return (
    <div
      className={`check-item${done ? ' done' : ''}`}
      style={highlight ? { borderColor: 'var(--brand-500)', background: 'var(--brand-50)' } : undefined}
    >
      {onToggle && (
        <button type="button" className={`cbox${done ? ' on' : ''}`} onClick={onToggle} aria-label={done ? 'Mark not done' : 'Mark done'}>
          <Icon name="check" strokeWidth={3} />
        </button>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="ct">{title}</div>
        {sub && <div className="cs">{sub}</div>}
      </div>
      {actions}
    </div>
  );
}

/* ------------------------------------------------------------------ insights */

export function Insight({ icon, tone = 'info', title, detail, children, plain }) {
  return (
    <div className="insight" style={plain ? { background: 'var(--surface)' } : undefined}>
      {icon && (
        <div className="ins-ico" style={{ background: `var(--${tone}-bg)`, color: `var(--${tone})` }}>
          <Icon name={icon} strokeWidth={2} />
        </div>
      )}
      <div style={{ minWidth: 0, flex: 1 }}>
        {title && <div className="ins-t">{title}</div>}
        {detail && <div className="ins-d">{detail}</div>}
        {children}
      </div>
    </div>
  );
}

export function AiCard({ tag = 'AI insight', title, children, actions }) {
  return (
    <div className="ai-card">
      <div className="flex items-c gap-10 mb-10">
        <span className="ai-tag"><Icon name="sparkles" />{tag}</span>
        {actions && <div className="ml-auto">{actions}</div>}
      </div>
      {title && <div style={{ fontWeight: 650, fontSize: 14, marginBottom: 9 }}>{title}</div>}
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------- states */

export function Empty({ icon = 'search', title = 'Nothing here yet', sub }) {
  return (
    <div className="empty">
      <Icon name={icon} strokeWidth={1.5} />
      <h4>{title}</h4>
      {sub && <p>{sub}</p>}
    </div>
  );
}

export function Loading({ label = 'Loading…' }) {
  return <div className="empty"><p>{label}</p></div>;
}

export function ErrorNote({ error }) {
  if (!error) return null;
  return (
    <div className="card mt-14" style={{ borderColor: 'var(--crit)' }}>
      <div className="card-b">
        <div style={{ fontWeight: 650, color: 'var(--crit)' }}>{error.message}</div>
        {error.details?.length > 0 && (
          <ul className="txt-sm muted" style={{ margin: '8px 0 0', paddingLeft: 18 }}>
            {error.details.map((d) => <li key={d.field}>{d.field}: {d.message}</li>)}
          </ul>
        )}
        {error.requestId && <div className="txt-sm muted mt-8">Request {error.requestId}</div>}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------- forms */

/**
 * Label, control and hint.
 *
 * The label is wired to the control by id. Callers almost never want to invent
 * one, so if the child has no id it is given a generated one and the label
 * points at that — otherwise the labels render as decoration and a screen reader
 * announces an unlabelled input. An explicitly passed id always wins.
 */
export function Field({ label, hint, children, htmlFor, full = false }) {
  const generated = useId();
  const child = isValidElement(children) ? children : null;

  // Only a real DOM element takes an id and answers to a label. A composite
  // child — a group of buttons, say — would swallow the prop and leave the
  // label pointing at nothing, so it gets a plain caption instead.
  const labellable = child !== null && typeof child.type === 'string';
  const id = htmlFor ?? (labellable ? child.props.id ?? generated : undefined);
  const described = hint ? `${id ?? generated}-hint` : undefined;

  const control = labellable && !child.props.id
    ? cloneElement(child, { id, ...(described ? { 'aria-describedby': described } : {}) })
    : children;

  return (
    <div className={`field${full ? ' field-full' : ''}`}>
      {label && (id ? <label htmlFor={id}>{label}</label> : <span className="field-label">{label}</span>)}
      {control}
      {hint && <div className="hint" id={described}>{hint}</div>}
    </div>
  );
}

export function Switch({ on, onChange }) {
  return (
    <button
      type="button"
      className={`switch${on ? ' on' : ''}`}
      onClick={() => onChange?.(!on)}
      role="switch"
      aria-checked={on}
    />
  );
}

/* ---------------------------------------------------------------- key/values */

export function KV({ items }) {
  return (
    <dl className="kv">
      {items.filter(Boolean).map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <dt>{k}</dt>
          <dd>{v ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Divider() {
  return <div className="divider" />;
}

/* --------------------------------------------------------------- pagination */

export function Pager({ meta, onPage }) {
  if (!meta || meta.pageCount <= 1) return null;
  return (
    <div className="flex items-c gap-10" style={{ padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
      <button type="button" className="btn btn-sm" disabled={meta.page <= 1} onClick={() => onPage(meta.page - 1)}>
        <Icon name="chevL" />Previous
      </button>
      <span className="txt-sm muted">Page {meta.page} of {meta.pageCount} · {num(meta.total)} records</span>
      <button type="button" className="btn btn-sm" disabled={meta.page >= meta.pageCount} onClick={() => onPage(meta.page + 1)}>
        Next<Icon name="chevR" />
      </button>
    </div>
  );
}

export { Icon };
